/**
 * PURPOSE: La ÚNICA representación de un partido del Centro de Programación (FTR-0028 WG3, §13).
 * RESPONSIBILITIES: Pintar una fila —selección, enfrentamiento, estado, programación— en los cuatro
 *   diseños que piden las vistas: `group`, `date`, `calendar` y `venue`.
 *
 *   UN COMPONENTE, CUATRO DISEÑOS. Duplicar el marcado en cada vista es cómo acaban divergiendo:
 *   un estado nuevo aparecería en tres vistas y faltaría en la cuarta. Aquí cambia la disposición,
 *   nunca la lectura del modelo ni las reglas de lo que se puede hacer.
 *
 *   EL SLOT SE MUESTRA SIEMPRE, EL ID NUNCA (§17). Antes del sorteo un partido es "A1 vs A4"; después
 *   es "Colegio X (A1)". Cuando el partido ya existe y el sorteo todavía no lo llenó, se dice "A
 *   definir" — que es información operativa, no un error.
 * COLLABORATORS: StateBadges.jsx, ScheduleCells.jsx, las cuatro vistas.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/components/MatchRow.jsx
 */
import React from "react";

import { displayMatchKey, legLabel, sideDisplay } from "../../../../utils/programmingCenterModel.js";
import RowBadges from "./StateBadges.jsx";
import { CourtCell, DateCell, TimeCell, VenueCell } from "./ScheduleCells.jsx";

const SideLabel = ({ side, align = "left" }) => {
  if (!side) return null;
  const { primary, secondary, pending } = sideDisplay(side);
  return (
    <span className={`flex min-w-0 flex-col ${align === "right" ? "items-end text-right" : ""}`}>
      <span
        className={`truncate text-sm font-black ${
          pending ? "text-amber-600" : "text-slate-900"
        }`}
        title={primary}
      >
        {primary}
      </span>
      {secondary ? (
        <span className="truncate text-[11px] font-bold uppercase tracking-wide text-slate-400">
          {secondary}
        </span>
      ) : null}
    </span>
  );
};

const Matchup = ({ row }) => {
  if (row.isBye) {
    return (
      <span className="flex min-w-0 items-center gap-2">
        <SideLabel side={row.home} />
        <span className="text-xs font-bold uppercase tracking-wide text-slate-400">descansa</span>
      </span>
    );
  }
  return (
    <span className="grid min-w-0 grid-cols-[1fr_auto_1fr] items-center gap-2">
      <SideLabel side={row.home} />
      <span className="text-[11px] font-black uppercase tracking-wide text-slate-400">vs</span>
      <SideLabel side={row.away} align="right" />
    </span>
  );
};

const SelectBox = ({ row, selected, onToggle }) => {
  if (!row.canSelect) {
    // Un BYE u huérfano no ocupa el hueco con una casilla apagada: se deja el espacio para que las
    // columnas sigan alineadas y quede claro que no participa de las acciones masivas.
    return <span className="inline-block h-4 w-4" aria-hidden="true" />;
  }
  return (
    <input
      type="checkbox"
      checked={selected}
      onChange={() => onToggle(row.iddraftmatch)}
      aria-label={`Seleccionar ${row.label}`}
      className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-400"
    />
  );
};

const ScheduleSummary = ({ row }) => (
  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-bold text-slate-500">
    <span>{row.date || "sin fecha"}</span>
    <span>·</span>
    <span>{row.time ? row.time.slice(0, 5) : "sin hora"}</span>
    {row.venueName ? (
      <>
        <span>·</span>
        <span className="truncate" title={row.venueName}>
          {row.venueName}
        </span>
      </>
    ) : null}
    {row.courtName ? (
      <>
        <span>·</span>
        <span className={row.courtRetired ? "text-amber-600" : ""}>
          {row.courtName}
          {row.courtRetired ? " (retirada)" : ""}
        </span>
      </>
    ) : null}
  </span>
);

const containerClass = (row, severity) => {
  if (severity === "error") return "border-rose-200 bg-rose-50/60";
  if (severity === "warning") return "border-amber-200 bg-amber-50/50";
  if (row.isOrphan) return "border-amber-200 bg-amber-50/40";
  if (row.isOfficialized) return "border-violet-200 bg-violet-50/40";
  if (row.isMaterialized) return "border-emerald-200 bg-emerald-50/30";
  if (row.isBye) return "border-slate-200 bg-slate-50";
  return "border-slate-200 bg-white";
};

/**
 * @param {"group"|"date"|"calendar"|"venue"} layout disposición; no cambia qué se puede hacer con la
 *   fila, solo cómo se coloca.
 */
const MatchRow = ({
  row,
  layout = "group",
  selected = false,
  onToggle = () => {},
  conflicts = [],
  severity = null,
  entry = null,
  venues = [],
  courtsByVenue = new Map(),
  onScheduleChange = null,
  disabled = false,
  actions = null,
}) => {
  const editable = Boolean(onScheduleChange) && row.canSchedule;
  const change = (field, value) => onScheduleChange?.(row, field, value);

  if (layout === "calendar") {
    return (
      <div
        className={`flex h-full min-w-0 flex-col justify-between gap-1 overflow-hidden rounded-lg border px-2 py-1 text-left shadow-sm ${containerClass(
          row,
          severity
        )}`}
        title={`${row.label}${row.groupLabel ? ` · ${row.groupLabel}` : ""}`}
      >
        <span className="truncate text-xs font-black text-slate-900">{row.label}</span>
        <span className="flex items-center justify-between gap-1">
          <span className="truncate text-[10px] font-bold uppercase tracking-wide text-slate-500">
            {row.groupLabel || row.tagFinal || "—"}
          </span>
          <span className="text-[10px] font-black text-slate-500">
            {row.time ? row.time.slice(0, 5) : ""}
          </span>
        </span>
      </div>
    );
  }

  if (layout === "venue") {
    return (
      <tr className={severity === "error" ? "bg-rose-50" : severity === "warning" ? "bg-amber-50" : ""}>
        <td className="px-2 py-1.5 print:hidden">
          <SelectBox row={row} selected={selected} onToggle={onToggle} />
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-xs font-black text-slate-500">
          {row.nroMatch ? `#${row.nroMatch}` : "—"}
        </td>
        <td className="px-2 py-1.5 text-xs font-bold text-slate-600">{row.date || "—"}</td>
        <td className="min-w-[220px] px-2 py-1.5">
          <Matchup row={row} />
        </td>
        <td className="w-28 px-2 py-1.5">
          {editable ? (
            <TimeCell row={row} entry={entry} onChange={change} disabled={disabled} />
          ) : (
            <span className="text-xs font-bold text-slate-600">
              {row.time ? row.time.slice(0, 5) : "—"}
            </span>
          )}
        </td>
        <td className="w-36 px-2 py-1.5">
          {editable ? (
            <CourtCell
              row={row}
              entry={entry}
              courtsByVenue={courtsByVenue}
              onChange={change}
              disabled={disabled}
            />
          ) : (
            <span className="text-xs font-bold text-slate-600">{row.courtName || "—"}</span>
          )}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-xs font-bold text-slate-600">
          {row.groupLabel || row.tagFinal || "—"}
        </td>
        <td className="px-2 py-1.5">
          <RowBadges row={row} conflicts={conflicts} />
        </td>
      </tr>
    );
  }

  const showFullSchedule = layout === "date";
  const keyLabel = displayMatchKey(row);
  // La ronda solo se nombra si el grupo tiene más de una: con una sola, "Ida" es tan poco
  // informativo como "L1" y obliga a interpretar una distinción que no existe.
  const legName = Number(row.legCount) > 1 ? legLabel(row.leg) : null;

  return (
    <div
      className={`flex flex-col gap-2 rounded-xl border px-3 py-2 transition ${containerClass(
        row,
        severity
      )}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <SelectBox row={row} selected={selected} onToggle={onToggle} />
        <span
          className="w-32 shrink-0 text-[11px] font-black uppercase leading-tight tracking-wide text-slate-400"
          title={keyLabel.title}
        >
          {keyLabel.text}
        </span>
        <div className="min-w-0 flex-1">
          <Matchup row={row} />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <RowBadges row={row} conflicts={conflicts} />
          {layout === "date" && (legName || row.groupLabel) ? (
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">
              {[legName, row.groupLabel].filter(Boolean).join(" · ")}
            </span>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
      </div>

      {showFullSchedule && row.canSchedule ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <DateCell row={row} entry={entry} onChange={change} disabled={disabled || !editable} />
          <TimeCell row={row} entry={entry} onChange={change} disabled={disabled || !editable} />
          <VenueCell
            row={row}
            entry={entry}
            venues={venues}
            onChange={change}
            disabled={disabled || !editable}
          />
          <CourtCell
            row={row}
            entry={entry}
            courtsByVenue={courtsByVenue}
            onChange={change}
            disabled={disabled || !editable}
          />
        </div>
      ) : !showFullSchedule && !row.isBye ? (
        <ScheduleSummary row={row} />
      ) : null}
    </div>
  );
};

export default MatchRow;
